import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "../../layouts/DashboardLayout";
import { addPayment } from "../../services/paymentService";
import { getPlans } from "../../services/membershipService";
import { getMembers } from "../../services/memberService";

import { FaCreditCard } from "react-icons/fa";

import "./AddPayments.css";

export default function AddPayment() {
  const navigate = useNavigate();

  // =========================================================
  // FORM DATA
  // =========================================================

  const [formData, setFormData] = useState({
    memberId: "",
    memberName: "",
    membershipPlan: "",
    amount: "",
    paymentDate: "",
    status: "Paid",
  });

  // =========================================================
  // DATA
  // =========================================================

  const [plans, setPlans] = useState([]);
  const [members, setMembers] = useState([]);

  const [loading, setLoading] = useState(true);

  // =========================================================
  // LOAD MEMBERS + PLANS
  // =========================================================

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const [membersData, plansData] = await Promise.all([
        getMembers(),
        getPlans(),
      ]);

      setMembers(membersData);
      setPlans(plansData);
    } catch (error) {
      console.error("Error loading payment data:", error);
      alert("Failed to load members or membership plans.");
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // HANDLE CHANGE
  // =========================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    let updatedData = {
      ...formData,
      [name]: value,
    };

    // =======================================================
    // MEMBER SELECTION
    // =======================================================

    if (name === "memberId") {
      const selectedMember = members.find(
        (member) => member.memberId === value,
      );

      if (selectedMember) {
        updatedData.memberId = selectedMember.memberId;

        updatedData.memberName = selectedMember.fullName || "";

        // Automatically select member's current plan
        if (selectedMember.membershipPlan) {
          updatedData.membershipPlan = selectedMember.membershipPlan;

          const selectedPlan = plans.find(
            (plan) =>
              plan.planName?.toLowerCase() ===
              selectedMember.membershipPlan?.toLowerCase(),
          );

          if (selectedPlan) {
            updatedData.amount = selectedPlan.price;
          }
        }
      }
    }

    // =======================================================
    // MEMBERSHIP PLAN
    // =======================================================

    if (name === "membershipPlan") {
      const selectedPlan = plans.find((plan) => plan.planName === value);

      updatedData.amount = selectedPlan ? selectedPlan.price : "";
    }

    setFormData(updatedData);
  };

  // =========================================================
  // SUBMIT
  // =========================================================

  const handleSubmit = async () => {
    // =======================================================
    // VALIDATION
    // =======================================================

    if (!formData.memberId) {
      alert("Please select a member.");
      return;
    }

    if (!formData.memberName.trim()) {
      alert("Member name is missing.");
      return;
    }

    if (!formData.membershipPlan) {
      alert("Please select Membership Plan.");
      return;
    }

    if (!formData.amount) {
      alert("Please enter Payment Amount.");
      return;
    }

    if (Number(formData.amount) <= 0) {
      alert("Payment Amount must be greater than 0.");
      return;
    }

    if (!formData.paymentDate) {
      alert("Please select Payment Date.");
      return;
    }

    if (!formData.status) {
      alert("Please select Payment Status.");
      return;
    }

    try {
      // =====================================================
      // PAYMENT DATA
      // =====================================================

      const paymentData = {
        memberId: formData.memberId,
        memberName: formData.memberName,
        membershipPlan: formData.membershipPlan,
        amount: Number(formData.amount),
        paymentDate: formData.paymentDate,
        status: formData.status,
        createdAt: new Date().toISOString(),
      };

      console.log("PAYMENT DATA:", paymentData);

      await addPayment(paymentData);

      alert("Payment Added Successfully.");

      navigate("/payments");
    } catch (error) {
      console.error("Error Adding Payment:", error);

      alert(error.message || "Error adding payment.");
    }
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <DashboardLayout>
      <div className="add-payment-page">
        {/* ===================================================
            HEADER
        =================================================== */}

        <div className="payment-header">
          <div>
            <h1>Add New Payment</h1>

            <p
              style={{
                fontWeight: "bold",
                color: "white",
              }}
            >
              Record membership payments and manage transaction details.
            </p>
          </div>
        </div>

        {/* ===================================================
            FORM CARD
        =================================================== */}

        <div className="payment-form-card">
          <div className="payment-icon">
            <FaCreditCard />
          </div>

          <h2>Payment Information</h2>

          <p className="payment-subtitle">
            Fill in the details below to create a payment record.
          </p>

          {/* =================================================
              MEMBER
          ================================================= */}

          <div className="form-group">
            <label>
              Member <span>*</span>
            </label>

            <select
              name="memberId"
              value={formData.memberId}
              onChange={handleChange}
              required
              disabled={loading}
            >
              <option value="">
                {loading ? "Loading Members..." : "Select Member"}
              </option>

              {members.map((member) => (
                <option key={member.id} value={member.memberId}>
                  {member.memberId} - {member.fullName}
                </option>
              ))}
            </select>
          </div>

          {/* =================================================
              MEMBER ID DISPLAY
          ================================================= */}

          {formData.memberId && (
            <div className="selected-member-info">
              <strong>Member ID:</strong> {formData.memberId}
              <br />
              <strong>Member:</strong> {formData.memberName}
            </div>
          )}

          {/* =================================================
              MEMBERSHIP PLAN
          ================================================= */}

          <div className="form-group">
            <label>
              Membership Plan <span>*</span>
            </label>

            <select
              name="membershipPlan"
              value={formData.membershipPlan}
              onChange={handleChange}
              required
            >
              <option value="">Select Membership Plan</option>

              {plans.map((plan) => (
                <option key={plan.id} value={plan.planName}>
                  {plan.planName} - ₹{plan.price}
                </option>
              ))}
            </select>
          </div>

          {/* =================================================
              AMOUNT
          ================================================= */}

          <div className="form-group">
            <label>
              Amount <span>*</span>
            </label>

            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              placeholder="Payment Amount"
              min="1"
              required
            />
          </div>

          {/* =================================================
              PAYMENT DATE
          ================================================= */}

          <div className="form-group">
            <label>
              Payment Date <span>*</span>
            </label>

            <input
              type="date"
              name="paymentDate"
              value={formData.paymentDate}
              onChange={handleChange}
              required
            />
          </div>

          {/* =================================================
              PAYMENT STATUS
          ================================================= */}

          <div className="form-group">
            <label>
              Payment Status <span>*</span>
            </label>

            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
            >
              <option value="Paid">Paid</option>

              <option value="Pending">Pending</option>

              <option value="Failed">Failed</option>
            </select>
          </div>

          {/* =================================================
              SAVE BUTTON
          ================================================= */}

          <button
            onClick={handleSubmit}
            className="payment-save-btn"
            disabled={loading}
          >
            {loading ? "Loading..." : "Save Payment"}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}
